import {
  CodatLinkOptions,
  ConnectionCallbackArgs,
  ErrorCallbackArgs,
} from "@codat/sdk-link-types";
import useQueryParams from "hooks/useQueryParams";
import { useState } from "react";
import { CodatLink } from "./CodatLink";

import styles from "./LinkSDK.module.scss";
import {
  useLinkApiService,
  usePublicApiService,
} from "contexts/ServiceContext";
import Loading from "components/Loading";
import { useQuery } from "@tanstack/react-query";
import { routes } from "config/urls";
import { AxiosError } from "axios";
import { formatUrl } from "utils";

type LinkSDKError = {
  errorCode?: number;
  errorMessage?: string;
  correlationId?: string;
};

type SearchParams = Record<string, string>;

export const LinkSDK = ({ companyId }: { companyId: string }) => {
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [linkSDKError, setLinkSDKError] = useState<LinkSDKError | null>(null);
  const publicApiService = usePublicApiService();
  const linkApiService = useLinkApiService();

  const queryParams = useQueryParams();
  const searchParams: SearchParams = Object.fromEntries(queryParams.entries());
  const {
    ["link.otp"]: otp,
    ["link.showSandboxIntegrations"]: showSandboxIntegrations,
    ["link.enableAdditionalConsent"]: enableAdditionalConsent,
    ["link.showLandingPage"]: showLandingPage,
    ["link.enableMultiEntityLinking"]: enableMultiEntityLinking,
    ...nonLinkSearchParams
  } = searchParams;

  const onError = ({
    errorCode,
    message,
    correlationId,
    userRecoverable,
  }: ErrorCallbackArgs) => {
    if (!userRecoverable) {
      setLinkSDKError({
        errorCode,
        errorMessage: message,
        ...(correlationId && { correlationId }),
      });
    }
  };

  const validateOtpQuery = useQuery({
    enabled: !!otp,
    refetchOnWindowFocus: false,
    queryKey: ["validateOtpQuery", { otp }],
    queryFn: () => publicApiService.validateOtp({ otp }),
    throwOnError: false,
  });

  const otpError = (() => {
    if (!validateOtpQuery.error) {
      return;
    }

    if (validateOtpQuery.error instanceof AxiosError) {
      const correlationId =
        validateOtpQuery.error?.response?.headers?.["x-correlation-id"] ||
        validateOtpQuery.error?.response?.headers?.["x-correlation-Id"];
      if (validateOtpQuery.error.status === 401) {
        return {
          errorCode: 401,
          message: "Unauthorized",
          correlationId,
        };
      } else {
        return {
          errorCode: 500,
          message: "Something went wrong",
          correlationId,
        };
      }
    }
  })();

  const customRedirectParams = (() => {
    if (linkSDKError) {
      const {
        errorCode,
        errorMessage: statusText,
        correlationId,
      } = linkSDKError;
      return getErrorQueryParams({
        nonLinkSearchParams,
        statusText,
        errorCode,
        connectionId,
        correlationId,
      });
    } else if (otpError) {
      const { errorCode, message: statusText, correlationId } = otpError;
      return getErrorQueryParams({
        nonLinkSearchParams,
        statusText,
        errorCode,
        connectionId,
        correlationId,
      });
    } else {
      return getErrorQueryParams({
        nonLinkSearchParams,
        connectionId,
      });
    }
  })();

  const getCustomRedirectQuery = useQuery({
    enabled: !!linkSDKError || !!validateOtpQuery.error,
    refetchOnWindowFocus: false,
    queryKey: ["getCustomRedirectQuery", { companyId, customRedirectParams }],
    queryFn: () =>
      linkApiService.getCustomRedirect({
        companyId,
        params: customRedirectParams,
      }),
  });

  if (getCustomRedirectQuery.data) {
    if (getCustomRedirectQuery.data.hasCustomRedirect) {
      location.assign(getCustomRedirectQuery.data.customRedirectUrl);
    } else if (!linkSDKError && !validateOtpQuery.error) {
      location.assign(routes.completed);
    } else if (linkSDKError || validateOtpQuery.error) {
      const {
        statusCode: errorCode,
        statusText: errorMessage,
        correlationId,
      } = customRedirectParams;

      const errorRedirectUrl = formatUrl(
        routes.error,
        {},
        {
          ...(errorCode && { errorCode }),
          ...(errorMessage && { errorMessage }),
          ...(correlationId && { correlationId }),
        }
      );

      location.assign(errorRedirectUrl);
    }
  }

  const onConnection = (args: ConnectionCallbackArgs) => {
    // file upload does not call on connection started so need to set connectionId here too
    setConnectionId(args.connectionId);
  };

  const onConnectionStarted = (args: ConnectionCallbackArgs) => {
    setConnectionId(args.connectionId);
  };
  const onFinish = () => {
    getCustomRedirectQuery.refetch();
  };

  /*
 do we need to fire if user wants to close the window before onConnection/onFinish was fired?
 import { useBeforeunload } from 'react-beforeunload';

 useBeforeunload(() => "Are you sure to close this tab?");
  */
  const accessToken = validateOtpQuery.data?.accessToken;
  const renderLinkSDK = !otp || (otp && accessToken);

  const options: CodatLinkOptions = getLinkOptionsFromQueryString({
    showSandboxIntegrations,
    enableAdditionalConsent,
    showLandingPage,
    enableMultiEntityLinking,
  });

  return (
    <div
      className={styles.modalWrapper}
      data-testid="link-sdk-wrapper"
      id="link-sdk-wrapper"
    >
      {renderLinkSDK ? (
        <CodatLink
          companyId={companyId}
          onConnection={onConnection}
          onError={onError}
          onFinish={onFinish}
          onConnectionStarted={onConnectionStarted}
          accessToken={accessToken}
          options={options}
        />
      ) : (
        <Loading dataTestId="loading-spinner" />
      )}
    </div>
  );
};

type OptionalErrorParams = LinkSDKError & {
  statusText?: string;
  connectionId?: string | null;
};

const getErrorQueryParams = ({
  nonLinkSearchParams,
  statusText,
  errorCode,
  connectionId,
  correlationId,
}: OptionalErrorParams & {
  nonLinkSearchParams: SearchParams;
}): OptionalErrorParams & SearchParams => ({
  ...nonLinkSearchParams,
  ...(statusText && { statusText }),
  ...(errorCode && { statusCode: String(errorCode) }),
  ...(connectionId && { connectionId }),
  ...(correlationId && { correlationId }),
});

const queryStringToBoolean = (value: string | undefined) =>
  !!value && value.toLowerCase() === "true";

type LinkQueryKeys = {
  [K in keyof CodatLinkOptions]: string | undefined;
};

type CustomizibleLinkKeys = Pick<
  LinkQueryKeys,
  | "enableAdditionalConsent"
  | "showLandingPage"
  | "showSandboxIntegrations"
  | "enableMultiEntityLinking"
>;

const getLinkOptionsFromQueryString = (
  linkOptionsKeys: CustomizibleLinkKeys
): CodatLinkOptions => {
  const options = Object.entries(linkOptionsKeys).reduce(
    (linkOptions, [key, value]) => {
      if (value) {
        return { ...linkOptions, [key]: queryStringToBoolean(value) };
      }
      return linkOptions;
    },
    {}
  );
  return options satisfies CodatLinkOptions;
};
